import { useState } from 'react';

import { useCreateNote } from '@/lib/hooks/useLms';
import { usePreviewStore } from '@/stores/previewStore';
import type { Note } from '@/lib/api/lms';

function formatDate(dateStr: string): string {
  return new Date(dateStr).toLocaleDateString('en-US', {
    month: 'short', day: 'numeric', year: 'numeric',
    hour: '2-digit', minute: '2-digit',
  });
}

interface NotesSectionProps {
  topicId: string;
  notes: Note[];
}

export function NotesSection({ topicId, notes }: NotesSectionProps) {
  const [content, setContent] = useState('');
  const createNote = useCreateNote(topicId);
  const previewActive = usePreviewStore((s) => s.active);

  const handleSubmit = async () => {
    const trimmed = content.trim();
    if (!trimmed) return;
    await createNote.mutateAsync(trimmed);
    setContent('');
  };

  return (
    <section className="rounded-xl border border-border bg-card p-4">
      <h2 className="mb-3 font-heading text-base font-bold">My Notes</h2>

      {/* Add note form */}
      <div className="mb-4">
        <textarea
          value={content}
          onChange={(e) => setContent(e.target.value)}
          placeholder="Add a note about this topic..."
          rows={3}
          disabled={previewActive}
          className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm resize-none focus:outline-none focus:ring-2 focus:ring-primary/50"
        />
        <div className="mt-2 flex justify-end">
          <button
            onClick={handleSubmit}
            disabled={createNote.isPending || !content.trim() || previewActive}
            title={previewActive ? 'Not available in student preview' : undefined}
            className="rounded-md bg-primary px-4 py-1.5 text-sm font-medium text-primary-foreground hover:bg-primary/90 disabled:opacity-50"
          >
            {createNote.isPending ? 'Saving...' : 'Save Note'}
          </button>
        </div>
      </div>

      {/* Existing notes */}
      {!notes || notes.length === 0 ? (
        <p className="text-sm text-muted-foreground">No notes yet. Add one above.</p>
      ) : (
        <div className="space-y-3">
          {notes.map((note) => (
            <div key={note.id} className="rounded-md bg-muted/50 p-3">
              <p className="text-sm whitespace-pre-wrap">{note.content}</p>
              <p className="mt-1.5 text-xs text-muted-foreground">
                {formatDate(note.created_at)}
              </p>
            </div>
          ))}
        </div>
      )}
    </section>
  );
}
